// The searchable time zone field. A select marked data-timezone-picker (Html.TimezonePickerFor) holds only the zone
// its form is bound to, so the form posts that zone whatever happens here. The picker loads every zone from
// api/timezones the first time it opens, or when a script sets a zone the field lacks.
import TomSelect from "tom-select";
import { browserZoneId, closedPieces, formatOffset, timeIn, timezoneOptions } from "./timezone-options.mjs";

let entriesRequest = null;

// One request per page, shared by every picker on it. A failed request is tried again on the next open.
function loadEntries() {
	if (entriesRequest === null) {
		entriesRequest = fetch("/api/timezones", {
			credentials: "same-origin",
			headers: { Accept: "application/json" },
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error(`api/timezones answered ${response.status}`);
				}
				return response.json();
			})
			.catch((error) => {
				entriesRequest = null;
				throw error;
			});
	}
	return entriesRequest;
}

const pickerStates = new WeakMap();

function fillList(picker) {
	const state = pickerStates.get(picker);
	if (state.loaded || state.loading) {
		return;
	}
	state.loading = true;
	loadEntries()
		.then((entries) => {
			const options = timezoneOptions(entries, picker.getValue(), browserZoneId(entries), state.language, new Date());
			options.forEach((option) => {
				if (picker.options[option.id]) {
					picker.updateOption(option.id, option);
				} else {
					picker.addOption(option);
				}
			});
			state.loaded = true;
			state.loading = false;
			picker.refreshOptions(false);
		})
		.catch(() => {
			// The field keeps the zone it holds; the next open tries again.
			state.loading = false;
		});
}

function renderOption(option, escape, language) {
	const country = option.country === ""
		? ""
		: `<span class="timezone-option-country">${escape(option.country)}</span>`;
	return `<div class="timezone-option">
		<div class="timezone-option-place">
			<div><span class="timezone-option-title">${escape(option.title)}</span>${country}</div>
			<div class="timezone-option-name">${escape(option.name)}</div>
		</div>
		<div class="timezone-option-now">
			<span>${escape(timeIn(option.id, language, new Date()))}</span>
			<span class="timezone-option-offset">${escape(formatOffset(option.offset))}</span>
		</div>
	</div>`;
}

// One line: the place and its offset first, so they show when the country and name are cut off. The stylesheet draws
// the dots between the pieces.
function renderItem(option, escape) {
	const [title, ...details] = closedPieces(option);
	const detailSpans = details.map((detail) => `<span class="timezone-item-detail">${escape(detail)}</span>`).join("");
	return `<div class="timezone-item"><span>${escape(title)}</span>${detailSpans}</div>`;
}

/** Turns a select marked data-timezone-picker into the searchable field; returns its Tom Select instance. */
export function attachTimezonePicker(select) {
	if (select.tomselect) {
		return select.tomselect;
	}
	const data = select.dataset;
	const language = data.timezoneLanguage || undefined;
	// The helper renders one option, the zone the form holds, and describes it on the select.
	const held = Array.from(select.options)
		.filter((option) => option.value !== "")
		.map((option) => ({
			id: option.value,
			city: data.timezoneCity,
			country: data.timezoneCountry,
			name: data.timezoneName,
		}));

	const picker = new TomSelect(select, {
		options: timezoneOptions(held, null, null, language, new Date()),
		items: select.value === "" ? [] : [select.value],
		optgroups: [
			{ value: "local", label: data.timezoneLocalGroup },
			{ value: "all", label: data.timezoneAllGroup },
		],
		optgroupField: "group",
		lockOptgroupOrder: true,
		valueField: "id",
		labelField: "title",
		searchField: [
			{ field: "title", weight: 3 },
			{ field: "name", weight: 2 },
			{ field: "country", weight: 2 },
			{ field: "search", weight: 1 },
		],
		sortField: [{ field: "$score" }, { field: "order" }],
		maxOptions: null,
		plugins: ["dropdown_input"],
		placeholder: data.timezoneSearch,
		render: {
			option: (option, escape) => renderOption(option, escape, language),
			item: (option, escape) => renderItem(option, escape),
			no_results: (query, escape) => `<div class="no-results">${escape(data.timezoneNoMatches)}</div>`,
		},
		onDropdownOpen() {
			fillList(this);
			// The rows show the current time: render them again on every open.
			this.clearCache();
			this.refreshOptions(false);
		},
	});
	pickerStates.set(picker, { language, loaded: false, loading: false });
	picker.control_input.setAttribute("aria-label", data.timezoneSearch);
	return picker;
}

/**
 * Sets the field to a zone, for scripts that fill a form (the shared view dialog). A zone the field lacks is offered
 * at once under its id, and named properly when the list arrives.
 */
export function setTimezonePickerValue(select, id) {
	const picker = attachTimezonePicker(select);
	if (!id) {
		picker.clear(true);
		return;
	}
	if (!picker.options[id]) {
		const state = pickerStates.get(picker);
		picker.addOption(timezoneOptions([], id, null, state.language, new Date())[0]);
		fillList(picker);
	}
	picker.setValue(id, true);
}

/**
 * The chosen zone in the words its closed field shows ("Lima · UTC-5 · Peru · Peru Time"), for scripts that name it
 * in text (an alert's time filter); empty when the field holds no zone.
 */
export function timezonePickerClosedText(select) {
	const picker = attachTimezonePicker(select);
	const option = picker.options[picker.getValue()];
	return option ? closedPieces(option).join(" · ") : "";
}

window.timezonePicker = {
	attach: attachTimezonePicker,
	setValue: setTimezonePickerValue,
	closedText: timezonePickerClosedText,
};

function attachAll() {
	document.querySelectorAll("select[data-timezone-picker]").forEach((select) => attachTimezonePicker(select));
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", attachAll);
} else {
	attachAll();
}
